import { Controller, useFormContext } from "react-hook-form";
import { z } from "zod";

import { Description, FieldError, Label } from "@/components/ui/field";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { SettingsGroupForm } from "./SettingsGroup";

/** Settings API stores missing values as null; coerce to "" for the form. */
const settingString = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((val) => val ?? "");

const optionalPrintSpeed = settingString.refine(
  (val) => val === "" || ["2", "3", "4", "5", "6"].includes(val),
  { error: "Must be 2–6 ips or printer default" },
);

const optionalDarkness = settingString.refine(
  (val) => {
    if (val === "") {
      return true;
    }
    const parsed = Number(val);
    return Number.isInteger(parsed) && parsed >= 0 && parsed <= 30;
  },
  { error: "Must be an integer from 0–30" },
);

const schema = z.object({
  printerDpi: settingString,
  printerOffsetLeftMm: settingString,
  printerOffsetTopMm: settingString,
  printerPrintSpeed: optionalPrintSpeed,
  printerDarkness: optionalDarkness,
});
type FormValues = z.infer<typeof schema>;

export function LabelSettings() {
  return (
    <SettingsGroupForm title="Label printing" schema={schema}>
      <FormFields />
    </SettingsGroupForm>
  );
}

const dpiOptions = [
  { label: "203 DPI (Standard)", value: "203" },
  { label: "300 DPI (High Resolution)", value: "300" },
];

const PRINT_SPEED_DEFAULT = "default";

const printSpeedOptions = [
  { label: "Printer default", value: PRINT_SPEED_DEFAULT },
  { label: "2 ips", value: "2" },
  { label: "3 ips", value: "3" },
  { label: "4 ips", value: "4" },
  { label: "5 ips", value: "5" },
  { label: "6 ips", value: "6" },
];

function FormFields() {
  const { control } = useFormContext<FormValues>();

  return (
    <div className="flex flex-col gap-2">
      <Controller
        control={control}
        name="printerDpi"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            aria-label="Printer DPI"
            className="max-w-sm"
            {...field}
            selectedKey={field.value}
            onSelectionChange={(val) => field.onChange(val)}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Printer DPI</Label>
            <SelectTrigger />
            <SelectList items={dpiOptions}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <Controller
          control={control}
          name="printerOffsetLeftMm"
          render={({ field, fieldState: { error } }) => (
            <NumberField
              aria-label="Printer left offset (mm)"
              className="w-full"
              name={field.name}
              value={field.value ? Number(field.value) : 0}
              onChange={(val) => {
                const nextValue = Number.isNaN(val) ? 0 : val;
                field.onChange(nextValue.toString());
              }}
              onBlur={field.onBlur}
              step={0.1}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Left offset (mm)</Label>
              <NumberInput />
              <FieldError>{error?.message}</FieldError>
            </NumberField>
          )}
        />

        <Controller
          control={control}
          name="printerOffsetTopMm"
          render={({ field, fieldState: { error } }) => (
            <NumberField
              aria-label="Printer top offset (mm)"
              className="w-full"
              name={field.name}
              value={field.value ? Number(field.value) : 0}
              onChange={(val) => {
                const nextValue = Number.isNaN(val) ? 0 : val;
                field.onChange(nextValue.toString());
              }}
              onBlur={field.onBlur}
              step={0.1}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Top offset (mm)</Label>
              <NumberInput />
              <FieldError>{error?.message}</FieldError>
            </NumberField>
          )}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        + moves right/down, - moves left/up.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <Controller
          control={control}
          name="printerPrintSpeed"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Select
              aria-label="Print speed"
              className="w-full"
              {...field}
              selectedKey={
                field.value === undefined ||
                field.value === null ||
                field.value === ""
                  ? PRINT_SPEED_DEFAULT
                  : field.value
              }
              onSelectionChange={(val) => {
                if (val === null || val === PRINT_SPEED_DEFAULT) {
                  field.onChange("");
                  return;
                }
                field.onChange(String(val));
              }}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Print speed</Label>
              <SelectTrigger />
              <SelectList items={printSpeedOptions}>
                {(item) => (
                  <SelectItem id={item.value}>{item.label}</SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />

        <Controller
          control={control}
          name="printerDarkness"
          render={({ field, fieldState: { error } }) => (
            <NumberField
              aria-label="Print darkness"
              className="w-full"
              name={field.name}
              value={
                field.value === undefined ||
                field.value === null ||
                field.value === ""
                  ? Number.NaN
                  : Number(field.value)
              }
              onChange={(val) => {
                if (Number.isNaN(val)) {
                  field.onChange("");
                  return;
                }
                field.onChange(String(val));
              }}
              onBlur={field.onBlur}
              minValue={0}
              maxValue={30}
              step={1}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Darkness (0–30)</Label>
              <NumberInput />
              <FieldError>{error?.message}</FieldError>
            </NumberField>
          )}
        />
      </div>

      <Description>
        Use printer default for speed, or clear darkness, to stop sending those
        overrides. The printer keeps its last commanded values until something
        else changes them. Applied only for Labels V2 printing.
      </Description>
    </div>
  );
}
